import { formatCurrency } from "@/lib/format";

function Section({ title, items, total, totalClass = "", emphasize = false }) {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="px-4 sm:px-6 py-3 bg-slate-50/60 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">{title}</span>
      </div>
      {items.length === 0 ? (
        <div className="px-4 sm:px-6 py-3 text-sm text-slate-400">No items</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it, idx) => (
            <li key={idx} className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
              <span className="text-sm text-slate-700 truncate">{it.name}</span>
              <span className="text-sm font-medium text-slate-900 tabular-nums">{formatCurrency(it.amount)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className={`px-4 sm:px-6 py-3 flex items-center justify-between border-t border-slate-200 bg-white ${emphasize ? "bg-slate-50" : ""}`}>
        <span className="text-sm font-semibold text-slate-800">Total {title}</span>
        <span className={`text-sm font-bold tabular-nums ${totalClass || "text-slate-900"}`}>{formatCurrency(total)}</span>
      </div>
    </div>
  );
}

export default function PnLTable({ data }) {
  if (!data) return null;
  const s = data.sections || {};
  const sum = data.summary || {};

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="pnl-table">
      <div className="px-4 sm:px-6 py-4 border-b border-slate-200">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Profit & Loss</div>
        <div className="font-display text-xl font-semibold text-slate-900 mt-0.5">{data.company || "Statement"}</div>
        {data.period && <div className="text-xs text-slate-500 mt-0.5">{data.period}</div>}
      </div>

      <Section title="Income" items={s.income?.items || []} total={s.income?.total || 0} />
      <Section title="Cost of Goods Sold" items={s.cogs?.items || []} total={s.cogs?.total || 0} />

      <div className="px-4 sm:px-6 py-3 flex items-center justify-between bg-[#1f64c2]/5 border-y border-slate-200">
        <span className="text-sm font-semibold text-[#1f64c2]">Gross Profit</span>
        <span className="text-base font-bold text-[#1f64c2] tabular-nums">{formatCurrency(sum.gross_profit || 0)}</span>
      </div>

      <Section title="Expenses" items={s.expenses?.items || []} total={s.expenses?.total || 0} />

      {(s.other_income?.items?.length || s.other_expense?.items?.length) ? (
        <>
          <Section title="Other Income" items={s.other_income?.items || []} total={s.other_income?.total || 0} />
          <Section title="Other Expense" items={s.other_expense?.items || []} total={s.other_expense?.total || 0} />
        </>
      ) : null}

      <div className="px-4 sm:px-6 py-4 flex items-center justify-between bg-slate-900 text-white">
        <span className="text-sm font-semibold">Net Income</span>
        <span className={`text-lg font-bold tabular-nums ${(sum.net_income || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
          {formatCurrency(sum.net_income || 0)}
        </span>
      </div>
    </div>
  );
}
